import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import type { ReactNode } from 'react'
import { doc, onSnapshot, Timestamp, collection, query, where, getDocs, updateDoc, setDoc, getDoc, type DocumentSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import type { WeeklySchedule, ArtistAvailability, TimeSlot } from '@/types/booking'
import { DEFAULT_WEEKLY_SCHEDULE } from '@/types/booking'
import type { ChatSettings } from '@/types/models'
import { setupGoogleCalendarWatch } from '@/lib/googleCalendarBackendSync'

interface BookingSubscription {
  startDate: Timestamp
  status: 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial'
  includeFakeChats?: boolean
}

export interface GoogleCalendarEvent {
  id: string
  start: Date
  end: Date
  summary: string
}

interface GoogleCalendarState {
  isConnected: boolean
  email?: string
  lastSynced?: Date
  events?: GoogleCalendarEvent[]
  accessToken?: string
  refreshToken?: string
  tokenExpiresAt?: number
}

interface IosCalendarState {
  isConnected: boolean
  lastSynced?: Date
  hasPermission: boolean
}

export interface WelcomeMessage {
  enabled: boolean
  content: string
  updatedAt?: Date
}

export interface Booking {
  id: string
  artistId: string
  chatId: string
  userId: string
  clientUsername: string
  scheduledTimestamp: Timestamp
  createdAt: Timestamp
  type: 'custom' | 'flash'
  customDesignStatus?: string
  shortSummary?: string
  estimatedTime?: number // in hours
  googleCalendarEventId?: string
  iosCalendarEventId?: string
  status?: 'scheduled' | 'completed' | 'no_show' | 'cancelled'
  messageId?: string // Links booking to the specific availability message it was created from
  bookingCompleteForm?: {
    finalPrice?: number
    actualDate?: Timestamp
    notes?: string
    completedAt?: Timestamp
  }
}

interface BookingSystemContextType {
  hasBookingSystem: boolean
  isLoading: boolean
  error: string | null
  subscription: BookingSubscription | null
  availability: ArtistAvailability | null
  updateAvailability: (weekSchedule: WeeklySchedule, mode: 'month' | 'week') => Promise<void>
  updateAvailabilityMode: (mode: 'month' | 'week') => Promise<void>
  updateDateOverrides: (dates: Date[], timeSlots: TimeSlot[]) => Promise<void>
  removeDateOverride: (date: string) => Promise<void>
  updateMonthSchedule: (dates: Date[], timeSlots: TimeSlot[], isUnavailable: boolean) => Promise<void>
  removeMonthScheduleOverride: (date: string) => Promise<void>
  artistId: string | null
  googleCalendar: GoogleCalendarState
  iosCalendar: IosCalendarState
  connectGoogleCalendar: () => Promise<void>
  disconnectGoogleCalendar: () => Promise<void>
  refreshGoogleCalendar: () => Promise<void>
  connectIosCalendar: () => Promise<void>
  disconnectIosCalendar: () => Promise<void>
  syncIosCalendarToAvailability: () => Promise<void>
  getBookingByChatId: (chatId: string) => Promise<Booking | null>
  subscribeToBookingByChatId: (chatId: string, onUpdate: (booking: Booking | null) => void) => (() => void)
  subscribeToBookingByMessageId: (chatId: string, messageId: string, onUpdate: (booking: Booking | null) => void) => (() => void)
  requiredInformation: any | null
  welcomeMessage: WelcomeMessage | null
  updateWelcomeMessage: (welcomeMessage: WelcomeMessage) => Promise<void>
  chatSettings: ChatSettings | null
}

const BookingSystemContext = createContext<BookingSystemContextType | undefined>(undefined)

export function BookingSystemProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthState()
  const { userData, loading: userProfileLoading, isArtist, artistId: profileArtistId } = useUserData()
  const [hasBookingSystem, setHasBookingSystem] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<BookingSubscription | null>(null)
  const [availability, setAvailability] = useState<ArtistAvailability | null>(null)
  const [artistId, setArtistId] = useState<string | null>(null)
  const [googleCalendar, setGoogleCalendar] = useState<GoogleCalendarState>({ isConnected: false })
  const [iosCalendar, setIosCalendar] = useState<IosCalendarState>({ isConnected: false, hasPermission: false })
  const [requiredInformation, setRequiredInformation] = useState<any | null>(null)
  const [welcomeMessage, setWelcomeMessage] = useState<WelcomeMessage | null>(null)
  const [chatSettings, setChatSettings] = useState<ChatSettings | null>(null)

  const connectGoogleCalendar = async () => {
    if (!artistId) {
      console.error('[BookingSystem] connectGoogleCalendar: No artist ID available')
      throw new Error('No artist ID available')
    }

    try {
      // Initialize Google OAuth client
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
      
      if (!clientId) {
        console.error('[BookingSystem] VITE_GOOGLE_CLIENT_ID is not configured')
        throw new Error('Google OAuth client ID is not configured')
      }
      
      console.log('[BookingSystem] Starting Google Calendar connection with clientId:', clientId.substring(0, 10) + '...')
      
      // Request calendar access + email/profile to get user info
      const scope = [
        'https://www.googleapis.com/auth/calendar',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ].join(' ')
      
      // Create OAuth popup window
      const width = 500
      const height = 600
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      
      // Generate a unique state token for this auth attempt
      const stateToken = `oauth_${Date.now()}_${Math.random().toString(36).substring(7)}`
      console.log('[BookingSystem] Generated state token:', stateToken)
      
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')
      authUrl.searchParams.set('client_id', clientId)
      authUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/google/callback`)
      authUrl.searchParams.set('response_type', 'token')
      authUrl.searchParams.set('scope', scope)
      authUrl.searchParams.set('state', stateToken)
      
      console.log('[BookingSystem] Opening OAuth popup with URL:', authUrl.toString().substring(0, 100) + '...')
      
      const popup = window.open(
        authUrl.toString(),
        'Google Calendar Auth',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      if (!popup) {
        throw new Error('Failed to open OAuth popup - it may have been blocked')
      }

      console.log('[BookingSystem] OAuth popup opened, starting to poll for result')

      // Return a promise that resolves when auth is complete
      return new Promise<void>((resolve, reject) => {
        // Poll for the auth result in localStorage
        let pollCount = 0
        const maxPolls = 120 // 2 minutes with 1-second intervals
        let pollError: Error | null = null
        let resolved = false // Track if we've already resolved
        
        // Listen for postMessage from popup as backup communication method
        const messageListener = (event: MessageEvent) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) {
            console.warn('[BookingSystem] Received message from unexpected origin:', event.origin)
            return
          }
          
          if (event.data.type === 'GOOGLE_OAUTH_SUCCESS' && event.data.state === stateToken) {
            console.log('[BookingSystem] Received OAuth success via postMessage')
            if (!resolved) {
              resolved = true
              clearInterval(pollForResult)
              window.removeEventListener('message', messageListener)
              
              const { access_token, email } = event.data.result
              
              // Process the auth result
              processAuthResult(access_token, email).then(resolve).catch(reject)
            }
          }
        }
        
        window.addEventListener('message', messageListener)
        
        // Helper function to process the auth result
        const processAuthResult = async (access_token: string, email: string) => {
          console.log('[BookingSystem] Processing auth token for email:', email)

          // Store the token and email in Firestore under bookingSystems/{artistId}
          const bookingSystemRef = doc(db, 'bookingSystems', artistId)
          console.log('[BookingSystem] Updating Firestore with auth details')
          
          await updateDoc(bookingSystemRef, {
            'googleCalendar.accessToken': access_token,
            'googleCalendar.email': email,
            'googleCalendar.isConnected': true,
            'googleCalendar.lastSynced': Timestamp.now(),
            'iosCalendar': null, // Disconnect iOS Calendar (either/or)
            'availability.iosOverrides': null // Clear iOS calendar overrides
          })
          
          console.log('[BookingSystem] Firestore updated, calling backend to setup watch')

          // Call backend to setup Google Calendar watch
          // Backend will receive webhooks when calendar changes
          // and update Firestore automatically
          try {
            const watchResponse = await setupGoogleCalendarWatch(access_token, artistId)
            if (!watchResponse.success) {
              console.error('[BookingSystem] Failed to setup calendar watch:', watchResponse.error)
              throw new Error(watchResponse.error || 'Failed to setup calendar watch')
            }
            console.log('[BookingSystem] Calendar watch setup successful')
          } catch (error) {
            console.error('[BookingSystem] Error setting up calendar watch:', error)
            // Still mark as connected even if watch setup fails - user can retry
            // Firestore listener will handle updates when they come
          }

          // Update local state - events will be populated by Firestore listener
          setGoogleCalendar({
            isConnected: true,
            email,
            accessToken: access_token,
            lastSynced: new Date(),
            events: []  // Will be populated by Firestore listener
          })
          setIosCalendar({ isConnected: false, hasPermission: false }) // Clear iOS Calendar
          
          console.log('[BookingSystem] Connection complete, watching for calendar updates via backend')
          
          if (popup && !popup.closed) {
            try {
              popup.close()
            } catch (e) {
              console.warn('[BookingSystem] Could not close popup window:', e)
            }
          }
        }
        
        const pollForResult = setInterval(async () => {
          if (resolved) {
            clearInterval(pollForResult)
            return
          }
          
          pollCount++
          
          try {
            const authResult = localStorage.getItem(`google_oauth_result_${stateToken}`)
            
            if (authResult) {
              resolved = true
              clearInterval(pollForResult)
              window.removeEventListener('message', messageListener)
              
              console.log('[BookingSystem] Auth result found in localStorage after', pollCount, 'polls')
              
              localStorage.removeItem(`google_oauth_result_${stateToken}`)
              
              const result = JSON.parse(authResult)
              
              if (result.error) {
                pollError = new Error(result.error)
                throw pollError
              }
              
              const { access_token, email } = result
              if (!access_token) {
                pollError = new Error('No access token received')
                throw pollError
              }

              await processAuthResult(access_token, email)
              resolve()
            } else if (pollCount >= maxPolls) {
              resolved = true
              clearInterval(pollForResult)
              window.removeEventListener('message', messageListener)
              pollError = new Error('OAuth timeout - no response from popup after 2 minutes')
              throw pollError
            } else if (pollCount % 10 === 0) {
              console.log('[BookingSystem] Still waiting for auth result... (poll', pollCount, ')')
            }
          } catch (error) {
            resolved = true
            clearInterval(pollForResult)
            window.removeEventListener('message', messageListener)
            console.error('[BookingSystem] Error in polling:', error)
            if (popup && !popup.closed) {
              try {
                popup.close()
              } catch (e) {
                console.warn('[BookingSystem] Could not close popup window:', e)
              }
            }
            reject(error)
          }
        }, 1000)
      })
      
    } catch (error) {
      console.error('[BookingSystem] Error connecting Google Calendar:', error)
      throw new Error('Failed to connect Google Calendar: ' + (error instanceof Error ? error.message : String(error)))
    }
  }

  /**
   * REMOVED: syncGoogleCalendar function
   * 
   * Google Calendar events are now synced via backend Watch API.
   * Backend receives webhooks from Google and updates Firestore automatically.
   * No direct API calls from frontend needed anymore.
   */

  const disconnectGoogleCalendar = async () => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'googleCalendar': null
      })

      setGoogleCalendar({ isConnected: false })
    } catch (error) {
      console.error('[BookingSystem] Error disconnecting Google Calendar:', error)
      throw new Error('Failed to disconnect Google Calendar')
    }
  }




  const refreshGoogleCalendar = async (): Promise<void> => {
    if (!googleCalendar.isConnected) {
      throw new Error('Google Calendar is not connected')
    }

    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      // Get the current access token from Firestore
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const bookingSystemSnap = await getDoc(bookingSystemRef)
      const accessToken = bookingSystemSnap.data()?.googleCalendar?.accessToken
      
      if (!accessToken) {
        throw new Error('Google Calendar access token not found')
      }

      // Call backend to trigger a manual sync
      // (backend already has watch setup, this just forces an immediate sync)
      console.log('[BookingSystem] Requesting manual refresh from backend')
      await setupGoogleCalendarWatch(accessToken, artistId)
      
      console.log('[BookingSystem] Google Calendar refreshed via backend')
    } catch (error) {
      console.error('[BookingSystem] Error refreshing Google Calendar:', error)
      
      // If token is expired, disconnect and ask user to reconnect
      if (error instanceof Error && error.message.includes('token expired')) {
        console.log('[BookingSystem] Detected expired token, clearing connection')
        try {
          const bookingSystemRef = doc(db, 'bookingSystems', artistId!)
          await updateDoc(bookingSystemRef, {
            'googleCalendar.isConnected': false,
            'googleCalendar.accessToken': null,
            'googleCalendar.email': null
          })
          
          setGoogleCalendar({ isConnected: false })
          throw new Error('Google Calendar access token expired. Please reconnect your calendar to continue.')
        } catch (disconnectError) {
          console.error('[BookingSystem] Error during token expiration cleanup:', disconnectError)
          throw error
        }
      }
      
      throw error
    }
  }

  const connectIosCalendar = async () => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      // Request calendar permissions
      const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar')
      
      console.log('[BookingSystem] Requesting full calendar access...')
      const result = await CapacitorCalendar.requestFullCalendarAccess()
      
      console.log('[BookingSystem] Calendar access result:', result.result)
      
      if (result.result === 'granted') {
        console.log('[BookingSystem] Calendar permission granted, updating Firestore...')
        
        // Update Firestore to mark iOS calendar as connected
        const bookingSystemRef = doc(db, 'bookingSystems', artistId)
        await updateDoc(bookingSystemRef, {
          'iosCalendar.isConnected': true,
          'iosCalendar.lastSynced': Timestamp.now(),
          'googleCalendar': null, // Disconnect Google Calendar (either/or)
          'availability.googleOverrides': null // Clear Google calendar overrides
        })

        setIosCalendar({ isConnected: true, hasPermission: true, lastSynced: new Date() })
        setGoogleCalendar({ isConnected: false }) // Clear Google Calendar
        
        console.log('[BookingSystem] iOS calendar connected successfully')
      } else if (result.result === 'denied') {
        throw new Error('Calendar permission was denied. Please enable calendar access in Settings.')
      } else if (result.result === 'prompt' || result.result === 'prompt-with-rationale') {
        throw new Error('Calendar permission prompt was not shown. Please try again.')
      } else {
        throw new Error(`Unexpected permission result: ${result.result}`)
      }
    } catch (error) {
      console.error('[BookingSystem] Error connecting iOS calendar:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to connect to iOS calendar')
    }
  }

  const disconnectIosCalendar = async () => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'iosCalendar': null
      })

      setIosCalendar({ isConnected: false, hasPermission: false })
    } catch (error) {
      console.error('[BookingSystem] Error disconnecting iOS calendar:', error)
      throw new Error('Failed to disconnect iOS calendar')
    }
  }

  const syncIosCalendarToAvailability = useCallback(async (): Promise<void> => {
    if (!artistId || !iosCalendar.isConnected) {
      console.log('[BookingSystem] iOS calendar not connected, skipping sync')
      return
    }

    try {
      console.log('[BookingSystem] Syncing iOS calendar to availability...')
      const { iosSyncCalendarToAvailability } = await import('@/lib/iosAvailabilitySync')
      await iosSyncCalendarToAvailability(artistId)
      
      // Update last synced time
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'iosCalendar.lastSynced': Timestamp.now()
      })

      setIosCalendar(prev => ({
        ...prev,
        lastSynced: new Date()
      }))

      console.log('[BookingSystem] iOS calendar synced successfully')
    } catch (error) {
      console.error('[BookingSystem] Error syncing iOS calendar:', error)
      throw error
    }
  }, [artistId, iosCalendar.isConnected])

  const updateAvailability = async (weekSchedule: WeeklySchedule, mode: 'month' | 'week' = 'week') => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await setDoc(bookingSystemRef, {
        availability: {
          mode,
          weekSchedule,
          dateOverrides: availability?.dateOverrides || {}
        }
      }, { merge: true })
    } catch (error) {
      console.error('[BookingSystem] Error updating availability:', error)
      throw new Error('Failed to update availability')
    }
  }

  const updateAvailabilityMode = async (mode: 'month' | 'week') => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'availability.mode': mode
      })
    } catch (error) {
      console.error('[BookingSystem] Error updating availability mode:', error)
      throw new Error('Failed to update availability mode')
    }
  }

  const updateDateOverrides = async (dates: Date[], timeSlots: TimeSlot[]) => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const dateOverrides = { ...(availability?.dateOverrides ?? {}) }
      
      // Format dates as YYYY-MM-DD strings and create override settings
      dates.forEach(date => {
        const dateString = format(date, 'yyyy-MM-dd')
        dateOverrides[dateString] = {
          isEnabled: timeSlots.length > 0,
          timeSlots: timeSlots
        }
      })

      await setDoc(bookingSystemRef, {
        availability: {
          mode: availability?.mode || 'week',
          weekSchedule: availability?.weekSchedule || DEFAULT_WEEKLY_SCHEDULE,
          dateOverrides
        }
      }, { merge: true })
    } catch (error) {
      console.error('[BookingSystem] Error updating date overrides:', error)
      throw new Error('Failed to update date overrides')
    }
  }

  const removeDateOverride = async (dateString: string) => {
    console.log('[BookingSystem] removeDateOverride called with:', dateString, 'artistId:', artistId)
    if (!artistId) {
      console.error('[BookingSystem] No artistId available!')
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const dateOverrides = { ...(availability?.dateOverrides ?? {}) }
      
      console.log('[BookingSystem] Current dateOverrides:', Object.keys(dateOverrides))
      
      // Remove the specified date override
      delete dateOverrides[dateString]
      
      console.log('[BookingSystem] After deletion:', Object.keys(dateOverrides))

      await updateDoc(bookingSystemRef, {
        'availability.dateOverrides': dateOverrides
      })
      
      console.log('[BookingSystem] Successfully updated Firestore')
    } catch (error) {
      console.error('[BookingSystem] Error removing date override:', error)
      throw new Error('Failed to remove date override')
    }
  }

  const updateMonthSchedule = async (dates: Date[], timeSlots: TimeSlot[], isUnavailable: boolean) => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      console.log('[BookingSystem] updateMonthSchedule called with:', {
        datesCount: dates.length,
        timeSlotCount: timeSlots.length,
        isUnavailable,
        dates: dates.map(d => format(d, 'yyyy-MM-dd'))
      })

      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const monthSchedule = { ...(availability?.monthSchedule ?? {}) }
      
      console.log('[BookingSystem] Current monthSchedule:', Object.keys(monthSchedule))
      
      // Format dates as YYYY-MM-DD strings and create schedule settings
      dates.forEach(date => {
        const dateString = format(date, 'yyyy-MM-dd')
        // If no time slots (empty array) OR isUnavailable, remove the date from schedule entirely
        if (timeSlots.length === 0 || isUnavailable) {
          console.log('[BookingSystem] Deleting date:', dateString)
          delete monthSchedule[dateString]
        } else {
          console.log('[BookingSystem] Updating date:', dateString, 'with', timeSlots.length, 'slots')
          monthSchedule[dateString] = {
            isEnabled: !isUnavailable,
            timeSlots: isUnavailable ? [] : timeSlots
          }
        }
      })

      console.log('[BookingSystem] Updated monthSchedule:', Object.keys(monthSchedule))

      // Use updateDoc to ensure proper nested field updates
      await updateDoc(bookingSystemRef, {
        'availability.monthSchedule': monthSchedule
      })
      
      console.log('[BookingSystem] Successfully updated monthSchedule in Firestore')
    } catch (error) {
      console.error('[BookingSystem] Error updating month schedule:', error)
      throw new Error('Failed to update month schedule')
    }
  }

  const removeMonthScheduleOverride = async (dateString: string) => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const monthSchedule = { ...(availability?.monthSchedule ?? {}) }
      
      delete monthSchedule[dateString]

      await updateDoc(bookingSystemRef, {
        'availability.monthSchedule': monthSchedule
      })
    } catch (error) {
      console.error('[BookingSystem] Error removing month schedule override:', error)
      throw new Error('Failed to remove month schedule override')
    }
  }

  const getBookingByChatId = async (chatId: string): Promise<Booking | null> => {
    try {
      const bookingsRef = collection(db, 'bookings')
      const bookingQuery = query(bookingsRef, where('chatId', '==', chatId))
      const querySnapshot = await getDocs(bookingQuery)
      
      if (querySnapshot.empty) {
        console.log('[BookingSystem] No booking found for chatId:', chatId)
        return null
      }

      // Get the first booking (should only be one per chat)
      const bookingDoc = querySnapshot.docs[0]
      const data = bookingDoc.data()
      
      console.log('[BookingSystem] Booking found for chatId:', chatId)
      // Extract clientUsername from clientData.Username or fall back to direct field for backward compatibility
      const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
      return {
        id: bookingDoc.id,
        artistId: data.artistId,
        chatId: data.chatId,
        userId: data.userId,
        clientUsername: clientUsername,
        scheduledTimestamp: data.scheduledTimestamp,
        createdAt: data.createdAt,
        type: data.type,
        customDesignStatus: data.customDesignStatus,
        shortSummary: data.shortSummary,
        status: data.status,
        estimatedTime: data.estimatedTime,
        googleCalendarEventId: data.googleCalendarEventId,
        iosCalendarEventId: data.iosCalendarEventId
      } as Booking
    } catch (error) {
      // Permission errors are expected for some users - just return null silently
      if (error instanceof Error && error.message.includes('permission')) {
        console.log('[BookingSystem] User does not have permission to query bookings (expected for non-artists)')
        return null
      }
      console.error('[BookingSystem] Error fetching booking by chatId:', error)
      return null
    }
  }

  const subscribeToBookingByChatId = useCallback((chatId: string, onUpdate: (booking: Booking | null) => void): (() => void) => {
    console.log('[BookingSystem] Setting up booking subscription for chatId:', chatId)
    
    const bookingsRef = collection(db, 'bookings')
    const bookingQuery = query(bookingsRef, where('chatId', '==', chatId))
    
    const unsubscribe = onSnapshot(bookingQuery, (querySnapshot) => {
      if (querySnapshot.empty) {
        console.log('[BookingSystem] No booking found for chatId (subscription):', chatId)
        onUpdate(null)
        return
      }

      // Get the most recent booking (sort by createdAt)
      const sortedDocs = querySnapshot.docs.sort((a, b) => {
        const aTime = (a.data().createdAt as Timestamp)?.toMillis() || 0
        const bTime = (b.data().createdAt as Timestamp)?.toMillis() || 0
        return bTime - aTime // Most recent first
      })
      const bookingDoc = sortedDocs[0]
      const data = bookingDoc.data()
      
      console.log('[BookingSystem] Booking updated for chatId:', chatId, 'status:', data.status)
      // Extract clientUsername from clientData.Username or fall back to direct field for backward compatibility
      const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
      const booking: Booking = {
        id: bookingDoc.id,
        artistId: data.artistId,
        chatId: data.chatId,
        userId: data.userId,
        clientUsername: clientUsername,
        scheduledTimestamp: data.scheduledTimestamp,
        createdAt: data.createdAt,
        type: data.type,
        customDesignStatus: data.customDesignStatus,
        shortSummary: data.shortSummary,
        status: data.status,
        estimatedTime: data.estimatedTime,
        googleCalendarEventId: data.googleCalendarEventId,
        iosCalendarEventId: data.iosCalendarEventId,
        messageId: data.messageId, // Link to the availability message
        bookingCompleteForm: data.bookingCompleteForm ? {
          finalPrice: data.bookingCompleteForm.finalPrice,
          actualDate: data.bookingCompleteForm.actualDate,
          notes: data.bookingCompleteForm.notes,
          completedAt: data.bookingCompleteForm.completedAt
        } : undefined
      } as Booking
      
      onUpdate(booking)
    }, (error) => {
      // Permission errors are expected for some users - just return null silently
      if (error instanceof Error && error.message.includes('permission')) {
        console.log('[BookingSystem] User does not have permission to subscribe to bookings (expected for non-artists)')
        onUpdate(null)
        return
      }
      console.error('[BookingSystem] Error in booking subscription:', error)
      onUpdate(null)
    })
    
    return unsubscribe
  }, [])

  /**
   * Subscribe to a specific booking by both chatId AND messageId.
   * This is more specific than subscribeToBookingByChatId and ensures each message
   * sees only its own booking, even when multiple bookings exist in the same chat.
   */
  const subscribeToBookingByMessageId = useCallback((chatId: string, messageId: string, onUpdate: (booking: Booking | null) => void): (() => void) => {
    console.log('[BookingSystem] Setting up booking subscription for chatId:', chatId, 'messageId:', messageId)
    
    const bookingsRef = collection(db, 'bookings')
    // Query for bookings that match BOTH chatId and messageId
    const bookingQuery = query(
      bookingsRef, 
      where('chatId', '==', chatId),
      where('messageId', '==', messageId)
    )
    
    const unsubscribe = onSnapshot(bookingQuery, (querySnapshot) => {
      if (querySnapshot.empty) {
        console.log('[BookingSystem] No booking found for chatId:', chatId, 'messageId:', messageId)
        onUpdate(null)
        return
      }

      // Should only be one booking per messageId, but if multiple exist, take the most recent
      const sortedDocs = querySnapshot.docs.sort((a, b) => {
        const aTime = (a.data().createdAt as Timestamp)?.toMillis() || 0
        const bTime = (b.data().createdAt as Timestamp)?.toMillis() || 0
        return bTime - aTime // Most recent first
      })
      const bookingDoc = sortedDocs[0]
      const data = bookingDoc.data()
      
      console.log('[BookingSystem] Booking updated for messageId:', messageId, 'status:', data.status)
      // Extract clientUsername from clientData.Username or fall back to direct field for backward compatibility
      const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
      const booking: Booking = {
        id: bookingDoc.id,
        artistId: data.artistId,
        chatId: data.chatId,
        userId: data.userId,
        clientUsername: clientUsername,
        scheduledTimestamp: data.scheduledTimestamp,
        createdAt: data.createdAt,
        type: data.type,
        customDesignStatus: data.customDesignStatus,
        shortSummary: data.shortSummary,
        status: data.status,
        estimatedTime: data.estimatedTime,
        googleCalendarEventId: data.googleCalendarEventId,
        iosCalendarEventId: data.iosCalendarEventId,
        messageId: data.messageId,
        bookingCompleteForm: data.bookingCompleteForm ? {
          finalPrice: data.bookingCompleteForm.finalPrice,
          actualDate: data.bookingCompleteForm.actualDate,
          notes: data.bookingCompleteForm.notes,
          completedAt: data.bookingCompleteForm.completedAt
        } : undefined
      } as Booking
      
      onUpdate(booking)
    }, (error) => {
      // Permission errors are expected for some users - just return null silently
      if (error instanceof Error && error.message.includes('permission')) {
        console.log('[BookingSystem] User does not have permission to subscribe to bookings (expected for non-artists)')
        onUpdate(null)
        return
      }
      console.error('[BookingSystem] Error in booking subscription for messageId:', messageId, error)
      onUpdate(null)
    })
    
    return unsubscribe
  }, [])

  const updateWelcomeMessage = async (welcomeMessage: WelcomeMessage) => {
    if (!artistId) {
      throw new Error('No artist ID available')
    }

    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'chatSettings.welcomeMessage': {
          enabled: welcomeMessage.enabled,
          content: welcomeMessage.content,
          updatedAt: Timestamp.now()
        }
      })
      
      setWelcomeMessage({
        ...welcomeMessage,
        updatedAt: new Date()
      })
      
      console.log('[BookingSystem] Welcome message updated')
    } catch (error) {
      console.error('[BookingSystem] Error updating welcome message:', error)
      throw new Error('Failed to update welcome message')
    }
  }

  // Set up listener for chatSettings from bookingSystems document
  useEffect(() => {
    if (!artistId) {
      setChatSettings(null)
      return
    }

    console.log('[BookingSystem] Setting up chatSettings listener for artist:', artistId)
    
    // Set up real-time listener for the bookingSystems document's chatSettings
    const unsubscribe = onSnapshot(
      doc(db, 'bookingSystems', artistId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          const settingsData = data?.chatSettings
          
          console.log('[BookingSystem] Raw chatSettings data from Firestore:', data?.chatSettings)
          console.log('[BookingSystem] chatSettings data:', settingsData)
          
          if (settingsData) {
            console.log('[BookingSystem] Chat settings loaded:', settingsData.mode)
            
            // Convert Timestamps to Dates
            const convertedSettings: ChatSettings = {
              mode: settingsData.mode,
              updatedAt: settingsData.updatedAt instanceof Timestamp 
                ? settingsData.updatedAt.toDate() 
                : settingsData.updatedAt instanceof Date 
                  ? settingsData.updatedAt
                  : undefined,
              welcomeMessage: settingsData.welcomeMessage ? {
                content: settingsData.welcomeMessage.content,
                updatedAt: settingsData.welcomeMessage.updatedAt instanceof Timestamp
                  ? settingsData.welcomeMessage.updatedAt.toDate()
                  : settingsData.welcomeMessage.updatedAt instanceof Date
                    ? settingsData.welcomeMessage.updatedAt
                    : undefined
              } : undefined,
              aiAssistant: settingsData.aiAssistant ? {
                enabled: settingsData.aiAssistant.enabled,
                personaPrompt: settingsData.aiAssistant.personaPrompt,
                maxAutoReplies: settingsData.aiAssistant.maxAutoReplies,
                handoverMessage: settingsData.aiAssistant.handoverMessage,
                updatedAt: settingsData.aiAssistant.updatedAt instanceof Timestamp
                  ? settingsData.aiAssistant.updatedAt.toDate()
                  : settingsData.aiAssistant.updatedAt instanceof Date
                    ? settingsData.aiAssistant.updatedAt
                    : undefined
              } : undefined
            }
            
            setChatSettings(convertedSettings)
          } else {
            console.log('[BookingSystem] No chat settings found in bookingSystems document')
            setChatSettings(null)
          }
        } else {
          console.log('[BookingSystem] bookingSystems document not found')
          setChatSettings(null)
        }
      },
      (error) => {
        console.error('[BookingSystem] Error listening to chat settings:', error)
        setChatSettings(null)
      }
    )

    return () => {
      console.log('[BookingSystem] Cleaning up chatSettings listener')
      unsubscribe()
    }
  }, [artistId])


  /**
   * Same wiring as `useUserData`'s booking listener: `users/{uid}` → `artistId`, then
   * `onSnapshot(bookingSystems/{artistId})`. No separate artist resolution or server-only pulls — avoids
   * fighting a second code path that kept marking access denied.
   */
  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false)
      setHasBookingSystem(false)
      setSubscription(null)
      setAvailability(null)
      setArtistId(null)
      setError(null)
      setGoogleCalendar({ isConnected: false })
      setIosCalendar({ isConnected: false, hasPermission: false })
      setRequiredInformation(null)
      setWelcomeMessage(null)
      setChatSettings(null)
      return
    }

    if (userProfileLoading || !userData) {
      setIsLoading(true)
      return
    }

    if (!isArtist || !profileArtistId) {
      setIsLoading(false)
      setHasBookingSystem(false)
      setSubscription(null)
      setAvailability(null)
      setArtistId(null)
      setError(null)
      setGoogleCalendar({ isConnected: false })
      setIosCalendar({ isConnected: false, hasPermission: false })
      setRequiredInformation(null)
      setWelcomeMessage(null)
      return
    }

    let isMounted = true
    setIsLoading(true)
    setError(null)
    setArtistId(profileArtistId)

    const bookingSystemRef = doc(db, 'bookingSystems', profileArtistId)

    const applyBookingSystemDocument = (bsSnapshot: DocumentSnapshot) => {
          if (!isMounted) return
          
          if (!bsSnapshot.exists()) {
            setIsLoading(false)
            setHasBookingSystem(false)
            setSubscription(null)
            setAvailability(null)
            setGoogleCalendar({ isConnected: false })
            
            // Clear cached booking system status
            localStorage.setItem('cachedBookingSystemStatus', JSON.stringify({
              hasBookingSystem: false,
              status: null,
              timestamp: Date.now()
            }))
            
            // Don't auto-create the document - wait until artist purchases the booking system
            return
          }

          const data = bsSnapshot.data()
          console.log('[BookingSystem] bookingSystems doc exists. Keys:', Object.keys(data || {}))
          const bookingSubscription = data?.bookingSubscription as BookingSubscription | undefined
          const bookingsAvailability = data?.availability as ArtistAvailability | undefined

          if (!bookingSubscription) {
            setHasBookingSystem(false)
            setSubscription(null)
            
            // Clear cached booking system status if no subscription found
            localStorage.setItem('cachedBookingSystemStatus', JSON.stringify({
              hasBookingSystem: false,
              status: null,
              timestamp: Date.now()
            }))
            
            // continue to handle availability regardless
          } else {
            // Check if subscription is active or trial (both grant access)
            const isActive = bookingSubscription.status === 'active' || bookingSubscription.status === 'trial'
            
            // Store the subscription data BEFORE logging to avoid any logging-time errors blocking state updates
            setSubscription(bookingSubscription)
            setHasBookingSystem(isActive)
            
            // Cache subscription status to localStorage for instant UI on app launch
            localStorage.setItem('cachedBookingSystemStatus', JSON.stringify({
              hasBookingSystem: isActive,
              status: bookingSubscription.status,
              timestamp: Date.now()
            }))

            // Safe logging (in case startDate is missing or not a Timestamp)
            const rawStartDate: any = (bookingSubscription as any)?.startDate
            const startDateValue = rawStartDate?.toDate ? rawStartDate.toDate() : rawStartDate ?? 'none'
            console.log(`[BookingSystem] BookingSystem ${bsSnapshot.id}: ${isActive ? '✅ Has access' : '❌ No access'} (${bookingSubscription.status})`)
            console.log('[BookingSystem] Subscription snapshot:', bookingSubscription)
            console.log('[BookingSystem] Subscription details:', {
              startDate: startDateValue,
              status: bookingSubscription.status
            })
          }

          // Handle availability data on bookingSystems document
          if (!bookingsAvailability) {
            // Don't auto-create availability - only set if document exists with data
            console.log('[BookingSystem] No availability data found in bookingSystems document')
            setAvailability(null)
          } else {
            console.log('[BookingSystem] Setting availability state with dateOverrides:', bookingsAvailability.dateOverrides ? Object.keys(bookingsAvailability.dateOverrides) : 'none')
            setAvailability(bookingsAvailability)
          }

          // Handle Google Calendar data
          const googleCalendarData = data?.googleCalendar
          console.log('[BookingSystem] googleCalendarData from Firestore:', googleCalendarData)
          
          if (googleCalendarData && googleCalendarData.accessToken) {
            console.log('[BookingSystem] Found Google Calendar connection in Firestore')
            // Parse events from Firestore (dates are stored as strings/ISO format)
            const rawEvents = googleCalendarData.events || []
            console.log('[BookingSystem] Raw events from Firestore:', rawEvents)
            
            const events: GoogleCalendarEvent[] = rawEvents.map((event: any) => {
              console.log('[BookingSystem] Parsing event:', event)
              return {
                id: event.id,
                start: event.start instanceof Date ? event.start : new Date(event.start),
                end: event.end instanceof Date ? event.end : new Date(event.end),
                summary: event.summary
              }
            })
            
            console.log('[BookingSystem] Parsed events:', events)
            
            // Convert Firestore Timestamp to Date
            let lastSyncedDate: Date | undefined
            if (googleCalendarData.lastSynced) {
              if (googleCalendarData.lastSynced instanceof Timestamp) {
                lastSyncedDate = googleCalendarData.lastSynced.toDate()
              } else if (googleCalendarData.lastSynced instanceof Date) {
                lastSyncedDate = googleCalendarData.lastSynced
              } else if (typeof googleCalendarData.lastSynced === 'string') {
                lastSyncedDate = new Date(googleCalendarData.lastSynced)
              } else if (typeof googleCalendarData.lastSynced === 'number') {
                lastSyncedDate = new Date(googleCalendarData.lastSynced)
              }
            }
            
            setGoogleCalendar({
              isConnected: true,
              email: googleCalendarData.email,
              accessToken: googleCalendarData.accessToken,
              lastSynced: lastSyncedDate,
              events
            })
            
            console.log('[BookingSystem] Google Calendar state restored:', {
              email: googleCalendarData.email,
              eventsCount: events.length,
              lastSynced: googleCalendarData.lastSynced
            })
          } else {
            console.log('[BookingSystem] No Google Calendar connection found')
            setGoogleCalendar({ isConnected: false })
          }

          // Handle iOS calendar state
          const iosCalendarData = data?.iosCalendar
          if (iosCalendarData?.isConnected) {
            let lastSyncedDate: Date | undefined
            if (iosCalendarData.lastSynced) {
              if (iosCalendarData.lastSynced instanceof Timestamp) {
                lastSyncedDate = iosCalendarData.lastSynced.toDate()
              } else if (iosCalendarData.lastSynced instanceof Date) {
                lastSyncedDate = iosCalendarData.lastSynced
              } else if (typeof iosCalendarData.lastSynced === 'string') {
                lastSyncedDate = new Date(iosCalendarData.lastSynced)
              } else if (typeof iosCalendarData.lastSynced === 'number') {
                lastSyncedDate = new Date(iosCalendarData.lastSynced)
              }
            }

            setIosCalendar({
              isConnected: true,
              hasPermission: true,
              lastSynced: lastSyncedDate
            })

            console.log('[BookingSystem] iOS calendar state restored:', {
              lastSynced: lastSyncedDate
            })
          } else {
            console.log('[BookingSystem] No iOS calendar connection found')
            setIosCalendar({ isConnected: false, hasPermission: false })
          }

          // Handle required information - support both new (bookingClientInfo) and legacy (bookingRequiredInformation) formats
          const bookingClientInfo = data?.bookingClientInfo
          const bookingRequiredInformation = data?.bookingRequiredInformation
          
          if (bookingClientInfo) {
            console.log('[BookingSystem] Setting required information from bookingClientInfo:', bookingClientInfo.length)
            setRequiredInformation(bookingClientInfo)
          } else if (bookingRequiredInformation) {
            console.log('[BookingSystem] Setting required information from bookingRequiredInformation:', Object.keys(bookingRequiredInformation))
            setRequiredInformation(bookingRequiredInformation)
          }

          // Handle welcome message settings from chatSettings
          const chatSettingsData = data?.chatSettings
          let welcomeMessageToSet: WelcomeMessage = {
            enabled: false,
            content: ''
          }
          
          if (chatSettingsData?.welcomeMessage) {
            const welcomeMessageData = chatSettingsData.welcomeMessage
            console.log('[BookingSystem] Welcome message found in chatSettings:', welcomeMessageData)
            let updatedDate: Date | undefined
            if (welcomeMessageData.updatedAt) {
              if (welcomeMessageData.updatedAt instanceof Timestamp) {
                updatedDate = welcomeMessageData.updatedAt.toDate()
              } else if (welcomeMessageData.updatedAt instanceof Date) {
                updatedDate = welcomeMessageData.updatedAt
              } else if (typeof welcomeMessageData.updatedAt === 'string') {
                updatedDate = new Date(welcomeMessageData.updatedAt)
              }
            }
            welcomeMessageToSet = {
              enabled: chatSettingsData.mode === 'welcome_message', // Enabled if mode is welcome_message
              content: welcomeMessageData.content ?? '',
              updatedAt: updatedDate
            }
          } else {
            console.log('[BookingSystem] No welcome message found in chatSettings')
          }
          
          setWelcomeMessage(welcomeMessageToSet)

          setIsLoading(false)
        }

    const unsubscribe = onSnapshot(
      bookingSystemRef,
      (snap) => {
        if (!isMounted) return
        applyBookingSystemDocument(snap)
      },
      (listenerError) => {
        console.error('[BookingSystem] bookingSystems snapshot error:', listenerError)
        if (!isMounted) return
        setError('Failed to load booking system')
        setIsLoading(false)
      }
    )

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [user?.uid, userProfileLoading, isArtist, profileArtistId])

  // Auto-sync iOS calendar every 30 minutes when connected
  useEffect(() => {
    if (!iosCalendar.isConnected || !artistId) {
      return
    }

    console.log('[BookingSystem] Setting up iOS calendar auto-sync')

    // Initial sync
    const performSync = async () => {
      try {
        await syncIosCalendarToAvailability()
      } catch (error) {
        console.error('[BookingSystem] Error in iOS calendar auto-sync:', error)
        // Don't throw - let sync failures be non-blocking
      }
    }

    performSync()

    // Set up periodic sync every 5 minutes
    const syncInterval = setInterval(() => {
      console.log('[BookingSystem] Running periodic iOS calendar sync')
      performSync()
    }, 5 * 60 * 1000) // 5 minutes

    return () => {
      console.log('[BookingSystem] Cleaning up iOS calendar auto-sync')
      clearInterval(syncInterval)
    }
  }, [iosCalendar.isConnected, artistId, syncIosCalendarToAvailability])

  return (
    <BookingSystemContext.Provider value={{ 
      hasBookingSystem, 
      isLoading, 
      error,
      subscription,
      availability,
      updateAvailability,
      updateAvailabilityMode,
      updateDateOverrides,
      removeDateOverride,
      updateMonthSchedule,
      removeMonthScheduleOverride,
      artistId,
      googleCalendar,
      iosCalendar,
      connectGoogleCalendar,
      disconnectGoogleCalendar,
      refreshGoogleCalendar,
      connectIosCalendar,
      disconnectIosCalendar,
      syncIosCalendarToAvailability,
      getBookingByChatId,
      subscribeToBookingByChatId,
      subscribeToBookingByMessageId,
      requiredInformation,
      welcomeMessage,
      updateWelcomeMessage,
      chatSettings
    }}>
      {children}
    </BookingSystemContext.Provider>
  )
}

export function useBookingSystem() {
  const context = useContext(BookingSystemContext)
  if (context === undefined) {
    throw new Error('useBookingSystem must be used within a BookingSystemProvider')
  }
  return context
}
